import './ViewAllAttributes.scss';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Box } from "@mui/material";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";
import AttributesTable from './AttributesTable';
import DatasetDropdownHome from '../../shared/components/dataset-dropdown/DatasetDropdownHome';
import { useStickyHeader } from '../../shared/hooks/StickyHeader';
import { usePreviewData, useRowCount } from "../../services/home";
import { useCountryList } from '../../services/okta';
import errorIconCircle from '../../assets/icons/error-icon-circle.svg';
import { formatNumbers } from '../../utils/numberFormatters';
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

//format table names
const formatTableName = (name: string) => {
  return name
    .replace(/entity_|_table/gi, "")       // remove entity_/ _table
    .split(/_|(?=[A-Z])/)                  // split on underscores or capital letters
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

function ViewAllAttributes() {
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const navigate = useNavigate();
  const location = useLocation();
  const { createLink } = useSimulationParams();

  const locationState = location.state as { tableName?: string; selectedDataset?: string };
  const initialTable = locationState?.tableName || "";
  const initialDataset = locationState?.selectedDataset || "";

  const { data: countries = [], isLoading: isCountriesLoading } = useCountryList();
  const [selectedTable, setSelectedTable] = useState(initialTable);
  const [selectedDataset, setSelectedDataset] = useState(initialDataset);

  useEffect(() => {
    if (countries.length > 0 && !selectedDataset) {
      const exists = countries.some(c => c.code === initialDataset);
      setSelectedDataset(exists ? initialDataset : countries[0].code);
    }
  }, [countries,initialDataset, selectedDataset]);

  //Fetch all available tables from row count API
  const { data: dataRowCount, isPending: isPendingRows } = useRowCount({
    query: { countryCode: selectedDataset },
  });

  //Build table options dynamically
  const tableOptions = useMemo(() => {
    if (!dataRowCount?.data) return [];
    try {
      const parsed = JSON.parse(dataRowCount.data || "[]") // Parse the row-count API response (fallback to an empty array string).
        .filter((item: any) => { // Filter out tables that should not be shown for the selected dataset.
          if (!(selectedDataset.trim().toLowerCase() === "us")) return formatTableName(Object.keys(item)[0]).toLowerCase() !== "stirista"; // For non-USA datasets: hide the "Stirista" table.
          return true; // For USA: keep all tables.
        }); // End of dataset-specific filtering.

      return parsed.map((item: any) => {
        const table_name = Object.keys(item)[0];
        //console.log(table_name);
        return formatTableName(table_name);
      });
    } catch {
      return [];
    }
  }, [dataRowCount, selectedDataset]);

  // Ensure selectedTable always valid
  useEffect(() => {
    if (tableOptions.length > 0 && !selectedTable) {
      setSelectedTable(tableOptions[0]);
    }
  }, [tableOptions, selectedTable]);

  // Fetch preview data
  const { data: previewData, isLoading, isFetching, error } = usePreviewData({
    query: { tableName: selectedTable, countryCode: selectedDataset },
  });

  const parsedData = useMemo(() => {
    if (!previewData?.data) return [];
    if (Array.isArray(previewData.data)) return previewData.data;
    console.warn("Unexpected API response:", previewData);
    return [];
  }, [previewData]);

  const columns = useMemo(() => {
    if (!parsedData.length) return [];
    const sample = parsedData[0];
    return Object.keys(sample).map((key) => ({
      key,
      label: key,
      maxWidth: key === 'id' ? 40 : key.length > 12 ? 100 : 80,
      render: key === 'PII' ? (val: boolean) => (val ? "Yes" : "No") : undefined,
    }));
  }, [parsedData]);

  const rows = useMemo(() => {
    return parsedData.map((row: any, idx: number) => {
      const normalizedRow: any = { id: idx + 1 };
      Object.entries(row).forEach(([key, value]) => {
        if (key === "Completion" && typeof value === "number") normalizedRow[key] = `${value}%`;
        else if (key === "Cardinality" && typeof value === "number") normalizedRow[key] = formatNumbers(value);
        else if (typeof value === "boolean") normalizedRow[key] = value.toString();
        else normalizedRow[key] = value;
      });
      return normalizedRow;
    });
  }, [parsedData]);

  return (
    <div className='view-all-attributes'>
      <div ref={sentinelRef}></div>

      <div ref={headerRef} className={`view-all-attributes__header ${isSticky ? 'is-sticky' : ''}`}>
        <div
          className="create-segment__wrap__heading-wrap"
          onClick={() => navigate(createLink("/"), { state: { selectedDataset } })}
        >
          <div className="create-segment__wrap__heading-wrap__icon">
            <img src={BackArrow} alt="Back" />
          </div>
          <div className="create-segment__wrap__heading-wrap__heading">Back to Home</div>
        </div>
        <div className='view-all-attributes__dataset-wrapper'>
          <div className='view-all-attributes__dataset-wrapper__label'>Attributes Table:</div>
          <FormControl>
            <Select
              value={selectedTable}
              onChange={(e: SelectChangeEvent) => setSelectedTable(e.target.value)}
            >
              {tableOptions.map((t: string) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>

          </FormControl>
        </div>
        <div className='view-all-attributes__dataset-wrapper'>
          <div className='view-all-attributes__dataset-wrapper__label'>Market:</div>
          {isCountriesLoading ? (
            <div>Loading countries...</div>
          ) : (
            <DatasetDropdownHome
              value={selectedDataset}
              onChange={(e: SelectChangeEvent) => setSelectedDataset(e.target.value)}
              countries={countries}
            />
          )}
        </div>
      </div>

      <div className='view-all-attributes__warning-msg'>
        <div className='view-all-attributes__warning-msg__wrap'>
          <img src={errorIconCircle} alt="Error Icon" style={{ cursor: "pointer" }} />
          <span>Some personally identifiable information has been redacted from these results.</span>
        </div>
      </div>

      <div className='view-all-attributes__dataset-wrap'>
        <div className='view-all-attributes__dataset-wrap__table'>
          {(isLoading || isFetching || isCountriesLoading || isPendingRows) ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
              <CircularProgressCustom />
            </Box>
          ) : error ? (
            <div>Error loading table data</div>
          ) : rows.length > 0 ? (
            <AttributesTable data={rows} columns={columns} />
          ) : (
            <div>No data available</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewAllAttributes;
